import apiService from '../services/APIService';
import Response from '../modules/classes/Response';

/**
 * Controller for profile view tracking and statistics
 * Matches mobile ProfileViewController implementation
 */
class ProfileViewController {
    constructor(api) {
        this.api = api;
    }

    getService() {
        return this.api || apiService;
    }

    /**
     * Record a profile view with 10-minute throttling
     * @param {string} username - Username of the user being viewed
     * @returns {Promise<Object>}
     */
    async trackProfileView(username) {
        return new Promise((resolve, reject) => {
            this.getService().post(`/users/${username}/view`)
                .then((res) => {
                    const response = Response.parse(res);
                    resolve(response.data);
                })
                .catch((err) => {
                    console.error('Error tracking profile view:', err);
                    reject(err);
                });
        });
    }

    /**
     * Get profile view statistics for a specific user
     * @param {string} username - Username of the user
     * @returns {Promise<Object>}
     */
    async getProfileViews(username) {
        return new Promise((resolve, reject) => {
            this.getService().get(`/users/${username}/views`)
                .then((res) => {
                    const response = Response.parse(res);
                    resolve(response.data);
                })
                .catch((err) => {
                    console.error('Error fetching profile views:', err);
                    reject(err);
                });
        });
    }

    /**
     * Get comprehensive profile view statistics
     * @returns {Promise<Object>}
     */
    async getStatistics() {
        return new Promise((resolve, reject) => {
            this.getService().get('/users/profile-views/statistics')
                .then((res) => {
                    const response = Response.parse(res);
                    resolve(response.data);
                })
                .catch((err) => {
                    console.error('Error fetching view statistics:', err);
                    reject(err);
                });
        });
    }

    /**
     * Get view history with filters
     * @param {Object} params - { page, limit, filterBy }
     * @returns {Promise<Object>}
     */
    async getHistory(params = {}) {
        return new Promise((resolve, reject) => {
            this.getService().get('/users/profile-views/history', { params })
                .then((res) => {
                    const response = Response.parse(res);
                    resolve(response.data);
                })
                .catch((err) => {
                    console.error('Error fetching view history:', err);
                    reject(err);
                });
        });
    }

    /**
     * Get view count for a specific period
     * @param {Date} startDate - Start date
     * @param {Date} endDate - End date
     * @returns {Promise<Object>}
     */
    async getViewCount(startDate, endDate) {
        return new Promise((resolve, reject) => {
            const params = {};
            if (startDate) params.startDate = startDate.toISOString();
            if (endDate) params.endDate = endDate.toISOString();

            this.getService().get('/users/profile-views/count', { params })
                .then((res) => {
                    const response = Response.parse(res);
                    resolve(response.data);
                })
                .catch((err) => {
                    console.error('Error fetching view count:', err);
                    reject(err);
                });
        });
    }

    /**
     * Get today's view count
     * @returns {Promise<number>}
     */
    async getTodayViewCount() {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        try {
            const result = await this.getViewCount(today, tomorrow);
            return result.count || 0;
        } catch (error) {
            console.error('Error fetching today view count:', error);
            return 0;
        }
    }

    /**
     * Get this week's view count
     * @returns {Promise<number>}
     */
    async getWeekViewCount() {
        const now = new Date();
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);

        try {
            const result = await this.getViewCount(weekAgo, now);
            return result.count || 0;
        } catch (error) {
            console.error('Error fetching week view count:', error);
            return 0;
        }
    }

    /**
     * Get this month's view count
     * @returns {Promise<number>}
     */
    async getMonthViewCount() {
        const now = new Date();
        const monthAgo = new Date(now);
        monthAgo.setDate(monthAgo.getDate() - 30);

        try {
            const result = await this.getViewCount(monthAgo, now);
            return result.count || 0;
        } catch (error) {
            console.error('Error fetching month view count:', error);
            return 0;
        }
    }
}

export default ProfileViewController;
